import { isNativeApp } from "@/lib/nativeBridge";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { usePublicPrices } from "@/lib/usePublicPrices";
import FooterQuickLinks from "@/components/FooterQuickLinks";
import StoreBadges from "@/components/StoreBadges";
import GlobalReachSection from "@/components/GlobalReachSection";
import { BusinessContactCard, DBA_NAME } from "@/components/BusinessContact";
import { SparkleIcon as Sparkle, ShieldCheckIcon as ShieldCheck, TreeStructureIcon as TreeStructure, VideoCameraIcon as VideoCamera, UsersIcon as Users, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle, DogIcon as Dog, MagnifyingGlassIcon as MagnifyingGlass, StorefrontIcon as Storefront, HeartStraightIcon as HeartStraight, BookOpenIcon as BookOpen, LockIcon as Lock, RobotIcon as Robot, SealCheckIcon as SealCheck, CalendarIcon as Calendar, TruckIcon as Truck, BellIcon as Bell, DropIcon as Drop, BabyIcon as Baby, DeviceMobileIcon as DeviceMobile, ScissorsIcon as Scissors, ConfettiIcon as Confetti, HouseIcon as House, FootprintsIcon as Footprints, GraduationCapIcon as GraduationCap, TreeIcon as Tree } from "@phosphor-icons/react";
import { NoiseOverlay, GoldDivider, WaxSeal, LuxuryCard, Icon } from "@/components/luxury";
import MarketingClipReel from "@/components/MarketingClipReel";
import JournalNewsletterCapture from "@/components/JournalNewsletterCapture";
import WidgetBoundary from "@/components/WidgetBoundary";
import OrganizationSchema from "@/components/OrganizationSchema";
import CampaignWelcomeBanner from "@/components/CampaignWelcomeBanner";

/**
 * Public landing page at `/`.
 *
 * Visible only to unauthenticated visitors — `RootRedirect` continues
 * to send authed users straight to /dashboard so the registry stays
 * the operator's home base.
 *
 * Goals:
 *   1. Above-the-fold: brand + tagline + CTA + 3-pillar trust strip
 *   2. Show real platform numbers (kennels + breeds count) — prevents
 *      the "ghost town" feel that synthetic landing pages have
 *   3. "Why we exist" — purebred + exotic registry framing without
 *      naming any other registry by name (keeps us venue-neutral)
 *   4. How it works — 3-step funnel: Sign up → Register → Trade safely
 *   5. Featured kennels strip — social proof from real members
 *   6. Final CTA strip — bold dark band, "Start your registry free"
 *
 * Authenticated users still get the full nav via PublicShell when they
 * land here (rare — RootRedirect skips them past).
 */
const TITLE = "Armstrong K9 Registry — the uncompromising standard for purebred and exotic dogs";
const DESCRIPTION =
  "The uncompromising standard for purebred and exotic dogs. Browse verified kennels, "
  + "audit pedigrees six generations deep, take live video tours, pay through Stripe-backed escrow, "
  + "book vetted trainers, groomers, walkers, sitters, and transporters, rent private Zoomie Time fields for off-leash play, sell food and merch from your storefront, "
  + "and run your kennel with a persona-aware calendar — all on a single venue.";

function Row({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-bold text-ink" : "text-[#3a3a3a]"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}


export default function LandingPage() {
  const { prices } = usePublicPrices();
  const storefrontMo = prices("storefront_monthly", "$19");
  const [stats, setStats] = useState({ kennels: 0, breeds: 0, available: 0, exotic: 0 });
  const [featured, setFeatured] = useState([]);
  // Tilt the medallion based on cursor position over the hero. Pure
  // CSS transform, no library, max ~6deg either axis — luxe, not arcade.
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    Promise.all([
      api.get("/public/kennels").catch(() => ({ data: { kennels: [], count: 0 } })),
      api.get("/public/breeds").catch(() => ({ data: { breeds: [] } })),
      api.get("/public/marketplace/stats").catch(() => ({ data: {} })),
    ]).then(([k, b, m]) => {
      const kennels = k.data?.kennels || [];
      const breeds = b.data?.breeds || [];
      setStats({
        kennels: k.data?.count ?? kennels.length,
        breeds: breeds.length,
        available: m.data?.available ?? 0,
        exotic: m.data?.exotic_listings ?? 0,
      });
      // Pull up to 6 production-looking kennels for the featured strip.
      // Hide obvious seed/dev rows ("Test Kennel", "Sample", etc.) and
      // any with zero registered dogs — surfacing those reads as a
      // ghost town. Falls back to the original list if filtering would
      // leave us with nothing (so we always have *something* to show
      // once at least one real kennel signs up).
      const SEED_RX = /\b(test|sample|demo|seed|example|placeholder)\b/i;
      const isReal = (kennel) => {
        const name = kennel.kennel_name || kennel.name || "";
        if (!name || SEED_RX.test(name)) return false;
        const dogs = kennel.dog_count ?? kennel.dogs_count ?? 0;
        if (Number(dogs) <= 0) return false;
        return true;
      };
      const real = kennels.filter(isReal);
      setFeatured(real.length > 0 ? real.slice(0, 6) : []);
    });
  }, []);

  // Mousemove handler scoped to the hero — calculates a normalized
  // -1..1 vector from center, which the medallion uses for a tiny 3D
  // rotateX/rotateY tilt + parallax translate. Disabled on touch.
  const onHeroMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;   // -1..1
    const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setTilt({ x: nx, y: ny });
  };
  const onHeroMouseLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink font-manrope">
      <CampaignWelcomeBanner />
      <OrganizationSchema />
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href="https://armstrongk9registry.com/" />
        <meta property="og:url" content="https://armstrongk9registry.com/" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://armstrongk9registry.com/og-video-tours.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Armstrong K9 Registry",
          url: "https://armstrongk9registry.com",
          logo: "https://armstrongk9registry.com/logo.png",
          description: DESCRIPTION,
        })}</script>
      </Helmet>

      {/* Header — transparent over the dark hero, with a subtle gold hairline
          and a backdrop blur on scroll. White text discipline matches the hero
          so there's no seam between header + hero. */}
      <header className="absolute top-0 inset-x-0 z-40 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="landing-brand">
            <span className="brand-light">
              <img src="/logo-256.png" alt="Armstrong K9 Registry"
                   className="h-11 w-11 object-contain" />
            </span>
            <span className="font-spectral text-base tracking-tight whitespace-nowrap hidden sm:inline text-white">
              Armstrong K9 Registry
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-9 text-[14px] tracking-wide text-white/75">
            <a href="#buyer" className="hover:text-gold transition-colors" data-testid="nav-marketplace">Marketplace</a>
            <a href="#how" className="hover:text-gold transition-colors" data-testid="nav-how">How it works</a>
            <a href="#kennels" className="hover:text-gold transition-colors" data-testid="nav-kennels">Kennels</a>
            <Link to="/video-tours" className="hover:text-gold transition-colors" data-testid="nav-video-tours">Video tours</Link>
            <Link to="/get-app" className="hover:text-gold transition-colors" data-testid="nav-get-app">Get the app</Link>
            <Link to="/pricing" className="hover:text-gold transition-colors" data-testid="nav-pricing">Pricing</Link>
            <Link to="/journal" className="hover:text-gold transition-colors" data-testid="nav-journal">Journal</Link>
            <Link to="/media" className="hover:text-gold transition-colors" data-testid="nav-media">Media</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="rounded-full text-[13px] text-white/80 hover:text-white hover:bg-white/10" data-testid="header-login">
                Sign in
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="rounded-full bg-white text-[#0E0E0E] hover:bg-gold hover:text-[#0E0E0E] text-[13px] font-medium shadow-[0_4px_18px_rgba(201,162,39,0.25)]" data-testid="header-register">
                Create account
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative overflow-hidden bg-ink text-white"
        data-testid="landing-hero"
        onMouseMove={onHeroMouseMove}
        onMouseLeave={onHeroMouseLeave}
      >
        <NoiseOverlay opacity={0.08} />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, rgba(201,162,39,0.28), transparent 50%), radial-gradient(circle at 15% 80%, rgba(193,39,45,0.18), transparent 55%)",
          }}
        />
        {/* Soft vignette darkens the corners — cinematic, not techy. */}
        <div className="pointer-events-none absolute inset-0 bg-vignette-dark" />

        {/* ============================================================
            Medallion SPOTLIGHT — lives at the section level (outside
            the grid) so the warm light bleeds across the WHOLE hero
            instead of being clipped to one column. This is what makes
            the medallion read as a real light source illuminating the
            dark room. Positioned to track where the medallion sits on
            each breakpoint (right-of-center on desktop, top-center on
            mobile). The breathing animation pulses it gently so it
            feels alive but never flashy.
            ============================================================ */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          {/* Top-left directional light cast — emits from the navbar
              logo's origin point and bleeds diagonally across the
              hero. This is the "logo as source of light across the
              page" treatment requested by the user; the medallion
              itself stays clean (no halo around it). */}
          <div className="hero-light-cast" />

          {/* Desktop / tablet spotlight — anchored where the medallion sits */}
          <div
            className="hidden md:block absolute rounded-full blur-3xl animate-medallion-aura"
            style={{
              width: "min(90vw, 1100px)",
              height: "min(90vw, 1100px)",
              top: "50%",
              right: "-12%",
              transform: "translateY(-50%)",
              background:
                "radial-gradient(circle at center, rgba(232,196,109,0.58) 0%, rgba(232,196,109,0.24) 22%, rgba(193,39,45,0.08) 48%, transparent 68%)",
              mixBlendMode: "screen",
            }}
          />
          {/* Subtle floor-light wash so the spill reaches the lower-left
              of the hero too — keeps the page from feeling lopsided. */}
          <div
            className="hidden md:block absolute rounded-full blur-3xl opacity-60 animate-medallion-aura"
            style={{
              width: "70vw",
              height: "70vw",
              bottom: "-30vw",
              right: "0%",
              background:
                "radial-gradient(circle at center, rgba(255,225,140,0.30), transparent 60%)",
              animationDelay: "2.2s",
              mixBlendMode: "screen",
            }}
          />
          {/* Mobile spotlight — top-center where the mobile medallion sits */}
          <div
            className="md:hidden absolute rounded-full blur-2xl animate-medallion-aura"
            style={{
              width: "140vw",
              height: "140vw",
              top: "-40vw",
              left: "50%",
              transform: "translateX(-50%)",
              background:
                "radial-gradient(circle at center, rgba(232,196,109,0.45) 0%, rgba(232,196,109,0.18) 22%, rgba(193,39,45,0.08) 45%, transparent 62%)",
              mixBlendMode: "screen",
            }}
          />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-[1fr,auto] gap-10 items-center">
          <div>
            {/* Mobile-only medallion — six layered cinematic effects so
                the brand mark reads as polished gold under stage lights,
                not a flat PNG. Everything is GPU-accelerated transform/
                opacity so it stays smooth on older iPhones, and the
                whole stack respects prefers-reduced-motion via the
                .animate-medallion-* classes (see index.css):
                  1. Ambient spotlight halo — radial gold wash with
                     5s breath cycle (medallion-aura). NOT a rectangle.
                  2. Slowly rotating conic rim — reads as light rolling
                     across the metal edge (medallion-spin-slow).
                  3. 6s float — gentle vertical drift so it feels alive
                     (medallion-float).
                  4. One-shot wax-seal stamp-in on page load — the disc
                     scales in from 0.82 / rotates from -7° with a soft
                     blur clear (medallion-stamp-in).
                  5. Sweep glint — diagonal cream band travels across
                     the metal every 4.5s, clipped inside the circular
                     mask (medallion-sweep).
                  6. Tight gold rim + close warm flare via the
                     `.brand-light > img` CSS shadow set so the disc
                     pops off the dark hero WITHOUT a rectangular
                     drop-shadow halo (the "weird box" the operator
                     called out on 2026-06-04). */}
            <div className="md:hidden flex justify-center mb-8 -mt-2">
              <div
                className="relative isolate"
                style={{ width: "min(58vw, 230px)", aspectRatio: "1 / 1" }}
              >
                {/* 1 — ambient spotlight. Sits OUTSIDE the medallion
                    silhouette so it reads as light EMITTED by the disc,
                    never wrapped tight around it. Negative margin
                    extends it beyond the wrapper bounds. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 -m-10 rounded-full blur-2xl animate-medallion-aura"
                  style={{
                    background:
                      "radial-gradient(circle at center, rgba(232,196,109,0.55) 0%, rgba(232,196,109,0.22) 28%, rgba(193,39,45,0.06) 55%, transparent 75%)",
                    mixBlendMode: "screen",
                  }}
                />
                {/* 2 — conic rim catch. Clipped to a thin ring via the
                    radial mask so only the EDGE picks up the light. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-full opacity-70 animate-medallion-spin-slow"
                  style={{
                    background:
                      "conic-gradient(from 0deg, transparent 0%, rgba(255,235,170,0.55) 14%, transparent 28%, rgba(255,225,140,0.42) 58%, transparent 78%)",
                    WebkitMask:
                      "radial-gradient(circle, transparent 58%, black 60%, black 72%, transparent 74%)",
                    mask: "radial-gradient(circle, transparent 58%, black 60%, black 72%, transparent 74%)",
                  }}
                />
                {/* 3 — float wrapper */}
                <div className="absolute inset-0 animate-medallion-float">
                  <a
                    href="/logo-compressed.png"
                    download="armstrong-k9-registry-logo.png"
                    aria-label="Download Armstrong K9 Registry medallion logo"
                    data-testid="hero-logo-download-mobile"
                    className="group relative block w-full h-full rounded-full animate-medallion-stamp-in focus:outline-none focus:ring-2 focus:ring-gold"
                  >
                    {/* 6 — clean circular mask + tight rim. Any rectangular
                        artifact in the source PNG gets clipped here. */}
                    <div
                      className="relative w-full h-full rounded-full overflow-hidden"
                      style={{
                        boxShadow:
                          "0 0 0 1px rgba(255,215,122,0.9), 0 1px 3px rgba(72,50,0,0.5), 0 0 28px rgba(255,220,138,0.45), 0 14px 32px rgba(0,0,0,0.35)",
                      }}
                    >
                      {/* WebP first for ~10× smaller payload on modern
                          devices, falls back to 512px PNG (60 KB) on
                          older Safari. Drops the previous 1.4 MB
                          /logo.png from the mobile critical path. */}
                      <picture>
                        <source srcSet="/logo.webp" type="image/webp" />
                        <img
                          src="/logo-512.png"
                          alt="Armstrong K9 Registry medallion"
                          loading="eager"
                          fetchpriority="high"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      </picture>
                      {/* 5 — sweep glint, clipped INSIDE the circular mask */}
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 rounded-full overflow-hidden"
                      >
                        <span
                          className="absolute top-0 left-0 h-[200%] w-[55%] -translate-x-1/2 -translate-y-1/4 animate-medallion-sweep"
                          style={{
                            background:
                              "linear-gradient(110deg, transparent 35%, rgba(255,250,225,0.55) 50%, transparent 65%)",
                            mixBlendMode: "screen",
                          }}
                        />
                      </span>
                    </div>
                    {/* Download affordance */}
                    <span
                      className="pointer-events-none absolute inset-x-0 -bottom-3 mx-auto w-max rounded-full bg-ink/85 backdrop-blur-sm border border-gold/40 px-3 py-1 text-[9px] uppercase tracking-[0.25em] font-bold text-gold-light opacity-0 group-active:opacity-100 group-hover:opacity-100 transition-opacity duration-300 shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
                    >
                      ↓ Download
                    </span>
                  </a>
                </div>
              </div>
            </div>
            <p
              className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-4 flex items-center gap-1.5"
              data-testid="landing-hero-tagline"
            >
              <Sparkle size={12} weight="fill" /> Pedigree · Passion · Purpose
            </p>
            <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
              The uncompromising standard for purebred and exotic dogs.
            </h1>
            <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed text-white/90 max-w-2xl">
              Armstrong K9 Registry is the one place serious dog people transact —
              buyers, breeders, trainers, groomers, walkers, sitters, USDA-licensed
              transporters, and storefront sellers, on a single set of receipts.
            </p>
            <p className="mt-4 text-[15px] md:text-[16px] leading-relaxed text-white/75 max-w-2xl">
              Browse a vetted marketplace of puppies and grown dogs. Audit any
              pedigree six generations deep with COI warnings flagged at
              registration. Pay through Stripe-backed escrow that only releases
              when the dog arrives. Book a verified trainer, groomer, walker, or
              sitter. Hire a USDA-licensed transporter. Open your own storefront.
              <span className="block mt-3 text-white/70">
                And if you&rsquo;re here because you just love your dog: track every
                meal, walk, weight, and milestone in one tap, share a live link with
                your sitter, and hand a clean PDF to your vet.
              </span>
            </p>
            <div className="mt-9 max-w-xl">
              <BreedSearchHero />
            </div>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <Link to="/shop">
                <Button
                  className="rounded-full bg-gold-gradient hover:opacity-95 text-ink font-semibold h-12 px-7 shadow-[0_8px_24px_rgba(201,162,39,0.35)] border-0"
                  data-testid="hero-shop-everything"
                >
                  Shop everything <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/register?intent=breeder">
                <Button
                  variant="outline"
                  className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-ink h-12 px-6"
                  data-testid="hero-register"
                >
                  Register your kennel
                </Button>
              </Link>
            </div>
            <StoreBadges tone="dark" source="hero" className="mt-7" />
            <div className="mt-8 flex items-center gap-x-6 gap-y-2 flex-wrap text-xs text-white/70">
              <span className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-gold-light" />
                Free to browse & register
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-gold-light" />
                Escrow-backed payments
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-gold-light" />
                Independent registry
              </span>
            </div>
          </div>
          <div className="hidden md:block relative isolate min-h-[400px] flex items-center justify-center">
            {/* The big section-level spotlight already lights the dark
                hero around the medallion — the layers below only add
                the TIGHT details that read as "polished metal up close":
                  1. Conic rim catch — slowly rotating light on the edge
                  2. Float wrapper   — gentle 6s breathing
                  3. Medallion + cursor specular + sweep glint */}

            {/* Medallion itself + cursor specular + sweep glint, wrapped
                in the float container so the whole thing breathes up
                and down 8px on a 6s cycle. */}
            <div className="relative animate-medallion-float">
              {/* Click-to-download anchor — `download` attribute forces a save
                  rather than a navigation, and the hover affordance surfaces a
                  small "↓ Download" pill so the action is discoverable without
                  cluttering the cinematic hero. The downloadable file is the
                  /logo-compressed.png variant (1024×1024, 256-color palette,
                  ~186 KB — visually identical to the master /logo.png but
                  under 512 KB so it attaches cleanly to email + social uploads
                  without further compression). */}
              <a
                href="/logo-compressed.png"
                download="armstrong-k9-registry-logo.png"
                aria-label="Download Armstrong K9 Registry medallion logo"
                data-testid="hero-logo-download"
                className="relative group block focus:outline-none focus:ring-2 focus:ring-gold rounded-full"
                style={{
                  transform: `perspective(900px) rotateX(${(-tilt.y * 6).toFixed(2)}deg) rotateY(${(tilt.x * 6).toFixed(2)}deg) translate3d(${(tilt.x * 6).toFixed(1)}px, ${(tilt.y * 6).toFixed(1)}px, 0)`,
                  transition: "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
                  transformStyle: "preserve-3d",
                  willChange: "transform",
                }}
              >
                <img
                  src="/logo.png"
                  alt="Armstrong K9 Registry medallion"
                  className="relative h-64 w-64 lg:h-80 lg:w-80 object-contain transition-transform duration-500 group-hover:scale-[1.04]"
                  style={{
                    // Stacked drop shadows: a tight close shadow grounds
                    // the medallion, a wider warm one creates volume, and
                    // a final crimson hint nods to the brand accent.
                    filter:
                      "drop-shadow(0 6px 14px rgba(0,0,0,0.45)) drop-shadow(0 18px 38px rgba(201,162,39,0.55)) drop-shadow(0 0 60px rgba(232,196,109,0.35))",
                  }}
                />
                {/* Specular highlight that follows the cursor — gives the
                    medallion a real "polished metal" feel. Pure radial-
                    gradient overlay; only renders when tilt is non-zero. */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-full mix-blend-screen"
                  style={{
                    background: `radial-gradient(circle at ${50 + tilt.x * 30}% ${50 + tilt.y * 30}%, rgba(255,245,210,0.55) 0%, rgba(255,225,140,0.18) 25%, transparent 50%)`,
                    opacity: 0.35 + Math.min(Math.hypot(tilt.x, tilt.y), 1) * 0.65,
                    transition: "opacity 220ms ease-out",
                  }}
                />
                {/* Sweeping glint — a diagonal band of bright cream
                    that travels across the medallion every ~4.5s.
                    Sits inside an overflow-hidden rounded mask so it
                    never bleeds outside the medallion silhouette. */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-full overflow-hidden"
                >
                  <span
                    className="absolute top-0 left-0 h-[200%] w-[55%] -translate-x-1/2 -translate-y-1/4 animate-medallion-sweep"
                    style={{
                      background:
                        "linear-gradient(110deg, transparent 35%, rgba(255,250,225,0.55) 50%, transparent 65%)",
                      mixBlendMode: "screen",
                    }}
                  />
                </span>
                <span
                  className="pointer-events-none absolute inset-x-0 -bottom-2 mx-auto w-max rounded-full bg-ink/85 backdrop-blur-sm border border-gold/40 px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold text-gold-light opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-300 shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
                >
                ↓ Download logo
              </span>
            </a>
            </div>
          </div>
        </div>
      </section>

      {/* Brand pillars — Pedigree. Passion. Purpose. The locked tagline
          surfaced as three explanatory cards, one per word. Lives between
          the hero and the live ticker so the abstract brand line gets a
          concrete payoff right where new visitors first scroll. */}
      <section
        className="relative bg-ink py-20 md:py-28 border-y border-gold/15"
        data-testid="landing-pillars"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light text-center mb-3">
            Built on three things
          </p>
          <h2 className="font-spectral font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-center text-white mb-14">
            Pedigree. Passion. Purpose.
          </h2>
          <div className="grid md:grid-cols-3 gap-8 md:gap-6">
            <article
              className="rounded-2xl border border-gold/20 bg-white/[0.03] backdrop-blur p-8 hover:border-gold/40 transition-colors"
              data-testid="landing-pillar-pedigree"
            >
              <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-gold mb-3">
                Pedigree
              </p>
              <h3 className="font-spectral text-2xl text-white mb-3 leading-tight">
                Six generations, audited.
              </h3>
              <p className="text-[15px] text-white/75 leading-relaxed">
                Every dog enters the registry with its lineage verified six
                generations deep. COI warnings flag inbreeding risk at
                registration &mdash; before a litter is on the ground.
              </p>
            </article>
            <article
              className="rounded-2xl border border-gold/20 bg-white/[0.03] backdrop-blur p-8 hover:border-gold/40 transition-colors"
              data-testid="landing-pillar-passion"
            >
              <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-gold mb-3">
                Passion
              </p>
              <h3 className="font-spectral text-2xl text-white mb-3 leading-tight">
                For the dog &mdash; not the deal.
              </h3>
              <p className="text-[15px] text-white/75 leading-relaxed">
                Buyers, breeders, and dog people who care about the
                relationship long after the handshake. Escrow-backed
                payments mean nobody walks away unfinished.
              </p>
            </article>
            <article
              className="rounded-2xl border border-gold/20 bg-white/[0.03] backdrop-blur p-8 hover:border-gold/40 transition-colors"
              data-testid="landing-pillar-purpose"
            >
              <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-gold mb-3">
                Purpose
              </p>
              <h3 className="font-spectral text-2xl text-white mb-3 leading-tight">
                One platform for every dog pro.
              </h3>
              <p className="text-[15px] text-white/75 leading-relaxed">
                Trainers, groomers, walkers, sitters, USDA-licensed
                transporters, and storefront sellers run their entire
                business here. Verified clients. Real reviews. Direct pay.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Live ticker — pulls marketplace stats (puppies live, exotic
          listings, breeds) plus kennel count. Below ~10 kennels we hide
          the integer and show editorial "Hand-audited" copy so a low
          count never reads as a ghost town. */}
      <section className="relative bg-cream" data-testid="landing-stats">
        <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gold-hairline" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-9">
          {/* Pulse ticker */}
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-[12px] uppercase tracking-[0.22em] text-[#3F3F3F] mb-8" data-testid="landing-pulse-ticker">
            <span className="inline-flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#3DA56E] opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#3DA56E]" />
              </span>
              <span className="font-semibold">Live</span>
            </span>
            <span className="hidden sm:inline-block w-px h-3.5 bg-[#D9CFB0]" />
            {stats.available > 0 ? (
              <span>
                <strong className="text-ink font-tabular">{stats.available}</strong> {stats.available === 1 ? "dog" : "dogs"} on the marketplace
              </span>
            ) : (
              <span>Marketplace open · escrow live</span>
            )}
            <span className="hidden sm:inline-block w-px h-3.5 bg-[#D9CFB0]" />
            {stats.exotic > 0 ? (
              <span>
                <strong className="text-ink font-tabular">{stats.exotic}</strong> exotic {stats.exotic === 1 ? "listing" : "listings"}
              </span>
            ) : (
              <span><strong className="text-ink font-tabular">{stats.breeds || 0}</strong> {stats.breeds === 1 ? "breed" : "breeds"} catalogued</span>
            )}
            <span className="hidden sm:inline-block w-px h-3.5 bg-[#D9CFB0]" />
            <span>Stripe-backed · audited 6 generations deep</span>
          </div>
          {/* 3 stat tiles */}
          <div className="grid grid-cols-3 gap-6">
            {stats.kennels >= 10 ? (
              <Stat icon={Users} value={stats.kennels} label="Verified kennels" />
            ) : (
              // Editorial frame: integer < 10 reads "ghost-town"; replace
              // with a curated label that still tells the trust story.
              <EditorialStat icon={Users} title="Hand-audited" sub="Every kennel passes a 7-check verification" />
            )}
            <Stat icon={Dog} value={stats.breeds} label="Distinct breeds" />
            <Stat icon={ShieldCheck} value="100%" label="Stripe-backed payments" suffix="" />
          </div>
        </div>
      </section>

      {/* Trust signals strip — independence + security cues. Quiet and
          institutional, not badge-spam. Designed to be skimmed in 1.5s
          while still telling a buyer "this is a serious operation." */}
      <section className="relative bg-white" data-testid="landing-trust-signals">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-7 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-[#6B6B6B]">
          <span className="inline-flex items-center gap-2">
            <ShieldCheck size={13} weight="duotone" className="text-gold" />
            Stripe-escrowed payments
          </span>
          <span className="hidden sm:inline-block w-px h-4 bg-[#E5E5E5]" />
          <span className="inline-flex items-center gap-2">
            <Lock size={13} weight="duotone" className="text-gold" />
            Two-factor authentication
          </span>
          <span className="hidden sm:inline-block w-px h-4 bg-[#E5E5E5]" />
          <span className="inline-flex items-center gap-2">
            <Robot size={13} weight="duotone" className="text-gold" />
            Cloudflare bot protection
          </span>
          <span className="hidden sm:inline-block w-px h-4 bg-[#E5E5E5]" />
          <span className="inline-flex items-center gap-2">
            <SealCheck size={13} weight="duotone" className="text-gold" />
            Independent of legacy registries
          </span>
        </div>
      </section>

      {/* Media & Press strip — auto-hides when nothing is featured. Lets
          the operator host promo videos, flyers, testimonials, etc.
          and keep traffic on-site instead of leaking to Instagram. */}
      <LandingMediaStrip />


      {/* Persona blocks — replaces the old "Three pillars" section.
          Four cards, one per audience (Breeder · Buyer · Transporter ·
          Owner). Each opens with what's in it for them — not what we
          do. The flagship feature for each persona is named explicitly
          so a visitor can see "yes, this is for me" in 4 seconds. */}
      <section className="py-20 md:py-28" data-testid="landing-personas">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
            Built for everyone in the registry
          </p>
          <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight max-w-3xl leading-[1.1]">
            For breeders, buyers, trainers, transporters, shops, and the people who simply love their dogs.
          </h2>
          <p className="mt-5 text-[15px] text-[#3F3F3F] max-w-2xl">
            One platform. Six audiences. Each gets the tools they need without seeing the ones they don't — all under one venue with a clear at-own-risk posture and Stripe-backed payments.
          </p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <PersonaCard
              icon={TreeStructure}
              eyebrow="For breeders"
              title="Run a serious kennel."
              features={[
                "Audited 4-generation pedigrees with COI inbreeding warnings",
                "Breeding matchmaker for compatible studs and dams",
                "Calendar auto-tracks heats, breedings, predicted whelp dates",
                "Upcoming-litter listings with waitlist deposits via Stripe Connect",
              ]}
              cta="Register your kennel"
              to="/register?intent=breeder"
            />
            <PersonaCard
              icon={MagnifyingGlass}
              eyebrow="For buyers"
              title="Buy your next dog. Hire the right people."
              features={[
                "Buy from verified kennels with audited pedigrees + Stripe escrow",
                "Hire trainers, groomers, walkers, sitters from one directory",
                "Book transport from USDA-licensed haulers with $300K liability",
                "Shop kennel storefronts — food, merch, custom orders",
                "Buyer Pass unlocks unlimited inquiries + Cmd+K global search",
              ]}
              cta="Shop everything"
              to="/shop"
              highlight
            />
            <PersonaCard
              icon={Scissors}
              eyebrow="For service providers"
              title="Trainers, groomers, walkers, sitters, field hosts."
              features={[
                "Searchable directory · identity verified · insurance & background-check badges (optional)",
                "Single & recurring bookings — Stripe Connect direct deposit",
                "Walkers & sitters can share a live GPS map with the owner during each walk",
                "Own land? Host Zoomie Time — rent your field by the hour for off-leash play",
                "Provider keeps 88% — buyer protection fee is added separately",
              ]}
              cta="Apply as a provider"
              to="/become-a-provider"
            />
            <PersonaCard
              icon={Truck}
              eyebrow="For transporters"
              title="Get paid for the miles you already drive."
              features={[
                "Vetted dispatch board with bid invitations",
                "Stripe Connect direct deposit on every completed haul",
                "Calendar tracks pickups, ETAs, and disputes in one view",
                "Escrow protection — buyers fund before you load",
              ]}
              cta="Apply as a transporter"
              to="/transporter"
            />
            <PersonaCard
              icon={Storefront}
              eyebrow="For storefronts"
              title="Sell food, merch, services."
              features={[
                "Your own /store/{slug} — Stripe Tax + Connect destination charges",
                "Local-delivery ZIP gating + nationwide shipping toggle",
                "Custom-order quoting flow for one-off requests",
                "Printify integration for on-demand merch",
              ]}
              cta="Open a storefront"
              to="/storefronts"
            />
            <PersonaCard
              icon={Dog}
              eyebrow="For dog owners"
              title="Care for the dog you already have."
              features={[
                "Dog Log — meals, walks, weight, meds across 10 categories",
                "Smart symptom warnings flag repeat issues for vet review",
                "Calendar of vet visits, training, vaccinations, and birthdays",
                "Live sitter link in one tap, Vet Visit PDF in another",
              ]}
              cta="Explore Dog Log"
              to="/dog-log"
            />
          </div>

          {/* Services strip — explicit catalog of what the platform
              actually does for any of the personas above. Keeps the
              "this is a real product" promise concrete after the
              persona promises. */}
          <div className="mt-20" data-testid="landing-services-strip">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
              Services on the platform
            </p>
            <h3 className="font-spectral font-light text-2xl md:text-4xl tracking-tight max-w-3xl leading-[1.15] mb-8">
              A single venue covering the entire dog economy.
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-[13.5px]">
              <ServiceTile
                icon={TreeStructure}
                title="Registry & pedigree"
                body="Dog registration, 4-gen pedigrees, COI inbreeding checks, microchip + DNA verification, exportable certificates."
              />
              <ServiceTile
                icon={MagnifyingGlass}
                title="Marketplace"
                body="Public listings for puppies + upcoming litters, breed catalog, global Cmd+K search, inquiry quotas, Buyer Pass."
              />
              <ServiceTile
                icon={Lock}
                title="Escrow & buyer protection"
                body="14-day Stripe escrow on every dog sale, separate buyer-protection fee, dispute resolution, refund routing."
              />
              <ServiceTile
                icon={HeartStraight}
                title="Breeding directory"
                body="Stud/dam matchmaking with COI warnings, live video tours via Daily.co, waitlist deposits on upcoming litters."
              />
              <ServiceTile
                icon={Scissors}
                title="Service providers"
                body="Trainers, groomers, walkers, sitters — discovery, single + recurring bookings, reviews, insurance verification."
              />
              <ServiceTile
                icon={Truck}
                title="Transport marketplace"
                body="USDA-licensed haulers, bid invitations, $300K liability minimum, 48-hour escrow protection on every booking."
              />
              <ServiceTile
                icon={Storefront}
                title="Storefronts (Dog Shop)"
                body="Per-kennel storefronts for food, merch, and custom orders with Stripe Tax + Printify integration."
              />
              <ServiceTile
                icon={Confetti}
                title="Events & shows"
                body="Public events feed, dog + handler registration, entry fees, iCal export, host broadcasts to registered handlers."
              />
              <ServiceTile
                icon={Dog}
                title="Dog Log & owner tools"
                body="Daily care tracking, sitter share links, symptom flags, vaccination reminders, Vet Visit PDF generator."
              />
            </div>
          </div>

          <GoldDivider variant="ornament" className="mt-20 max-w-md mx-auto" />
        </div>
      </section>

      {/* Real product footage — 6 Playwright-recorded clips, one per
          headline feature. Renders nothing if no clips are available
          yet (e.g. before the first GH Actions run on a fresh deploy),
          so the section won't leave an empty hole on the page. */}
      <MarketingClipReel />

      {/* What you can do here — buyer + breeder side. The pillars
          above are trust signals; this section is the actual journeys
          users come for: search the marketplace, get matched as a
          buyer, find a mate as a breeder, or browse the breed catalog.
          Cream paper tier so it reads as a distinct chapter from the
          white pillars section above. */}
      <section id="buyer" className="relative py-20 md:py-28 bg-cream" data-testid="landing-buyer">
        <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gold-hairline" />
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
                What you can do here
              </p>
              <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight max-w-3xl leading-[1.1]">
                Buy a verified dog — puppy or proven adult. Find a mate for your dog. Audit any pedigree. Track every walk and meal.
              </h2>
            </div>
            <Link to="/register" className="text-sm text-ink hover:text-crimson underline underline-offset-4">
              Create a free account →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            <BuyerCard
              icon={Storefront}
              title="The marketplace"
              text="Search currently-available puppies and grown dogs across every verified kennel — filter by breed, sex, price, and exotic-only listings."
              cta="Browse the marketplace"
              to="/marketplace"
            />
            <BuyerCard
              icon={HeartStraight}
              title="Find My Dog matching"
              text="Tell us what you want — temperament, climate, energy level, kid-friendliness — and we route you to vetted kennels working that line."
              cta="Get matched"
              to="/register?intent=buyer&next=/dashboard"
            />
            <BuyerCard
              icon={TreeStructure}
              title="Breeding Matchmaker"
              text="For breeders: pair your stud or dam with compatible registered dogs. Live COI scoring flags inbreeding risk before you commit to a pairing."
              cta="Find a stud or dam"
              to="/register?intent=breeder&next=/breeding"
            />
            <BuyerCard
              icon={BookOpen}
              title="Breed catalog"
              text="Sixty-five breeds with honest profiles: temperament, grooming, health screening, and which kennels are actively producing them."
              cta="Open the catalog"
              to="/breeds"
            />
            <BuyerCard
              icon={Dog}
              title="Dog Log — for dog lovers"
              text="Not buying. Not breeding. Just devoted. Track meals, walks, meds, weight, and symptoms in one tap. Share a live link with your sitter, hand a Vet Visit PDF to your vet."
              cta="Explore Dog Log"
              to="/dog-log"
            />
          </div>
        </div>
      </section>

      {/* Disruptive moment — single big serif line on ink, gold accent.
          Breaks the predictable rhythm between Buyer journey and the
          Service Pros chapter. Editorial, not decorative: states the
          house standard in one breath. */}
      <section
        className="relative bg-ink text-white overflow-hidden"
        data-testid="landing-manifesto"
      >
        <NoiseOverlay opacity={0.06} />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 70% 30%, rgba(201,162,39,0.22), transparent 55%), radial-gradient(circle at 20% 80%, rgba(193,39,45,0.12), transparent 50%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-36 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gold-light mb-8">
            What sets us apart
          </p>
          <blockquote className="font-spectral font-light text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.1] tracking-tight">
            Every pedigree audited.
            <span className="block mt-2 text-gold-light italic">
              Every payment escrowed.
            </span>
            <span className="block mt-2">Every promise in writing.</span>
          </blockquote>
          <p className="mt-10 text-[14px] md:text-[15px] text-white/65 max-w-2xl mx-auto leading-relaxed">
            The dog economy has always run on handshakes and screenshots.
            We made it run on receipts — six-generation pedigrees, COI
            inbreeding warnings flagged at registration, Stripe-backed
            escrow that only releases on arrival, and a verified
            background-check option on every service provider.
          </p>
          <div className="mt-12 flex items-center justify-center gap-4 text-[11px] uppercase tracking-[0.3em] text-white/55">
            <span className="h-px w-12 bg-gold/60" />
            <span>Armstrong K9 Registry</span>
            <span className="h-px w-12 bg-gold/60" />
          </div>
        </div>
      </section>

      {/* Service-pro section — dedicated chapter explaining what the
          four service-provider verticals look like ON the platform
          (training disciplines, grooming specialties, walker/sitter
          coverage) and what a pro actually gets when they join.
          Insertion point chosen so the page reads:
            Pillars → Buyer journeys → Service pros → Calendar (dark hero)
          which mirrors a buyer's natural funnel: trust → search → care. */}
      <section
        id="service-pros"
        className="relative py-20 md:py-28 bg-white"
        data-testid="landing-service-pros"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-[1fr,2fr] gap-12 items-start mb-14">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
                The people who care for your dog
              </p>
              <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.05]">
                Trainers, groomers, walkers, sitters, Zoomie Time fields — identity-verified, bookable, your way.
              </h2>
            </div>
            <div className="space-y-4 text-[15px] text-[#3F3F3F] leading-relaxed">
              <p>
                Bringing home a new dog is the start of a long relationship — with the
                dog and with everyone who'll help you raise them. Most buyers spend
                more on training, grooming, walking, and boarding over a dog's lifetime
                than they did on the dog itself. We surface that whole network in one
                place so you don't have to rebuild it from scratch each time you move,
                travel, or take on a new breed.
              </p>
              <p>
                Every service provider on Armstrong K9 Registry is identity-verified.
                Providers <em>may</em> upload an insurance certificate and run an
                optional paid background check — both surface as visible trust
                badges, but neither is required to list. Bookings run through the
                same Stripe escrow rails as the rest of the platform; disputes
                route to Stripe. <strong>Armstrong K9 Registry is the venue</strong>{" "}
                — providers are independent businesses, not employees, and you
                book at your own risk under our at-own-risk venue terms.
              </p>
            </div>
          </div>

          {/* Vertical-by-vertical breakdown so pros and customers can
              both see themselves on the page. Photo-less by design —
              an icon + a transparent-pricing band + a short trust
              signal. We deliberately don't quote dollar ranges here:
              prices are set by each independent provider on their
              profile, so any number printed on the landing page would
              be at best generic and at worst false advertising. */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
            <ProVerticalCard
              icon={GraduationCap}
              vertical="Training"
              typical="Provider sets pricing"
              examples="Puppy basics · obedience · protection · service-dog · sport"
              note="Single sessions or packaged programs. Recurring weekly sessions auto-charge with one consent."
            />
            <ProVerticalCard
              icon={Scissors}
              vertical="Grooming"
              typical="Provider sets pricing"
              examples="Bath & brush · breed-specific scissoring · de-shedding · show prep · mobile"
              note="Mobile and salon-based. Add-on services priced per add-on."
            />
            <ProVerticalCard
              icon={Footprints}
              vertical="Walking"
              typical="Provider sets pricing"
              examples="Solo or group · 30 / 60 min · pickup &amp; return"
              note="Subscribe weekly and the provider auto-charges with one consent at booking."
            />
            <ProVerticalCard
              icon={House}
              vertical="Sitting & Boarding"
              typical="Provider sets pricing"
              examples="In-your-home · at-their-home · drop-in visits · overnight boarding"
              note="Dog Log share-link auto-attached so the sitter sees feeding times, meds, and quirks."
            />
          </div>

          {/* Two columns — owner journey on the left, pro economics on
              the right. Mirrors the "two-sided marketplace" reality:
              the same page sells to both sides. */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-10">
            <div
              className="bg-cream/40 border border-[#E2DAC2] rounded-2xl p-7"
              data-testid="landing-service-pros-owner-side"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-2">
                For owners
              </p>
              <h3 className="font-spectral text-2xl mb-4 leading-tight">
                One directory. Trust badges. Live walk maps. Escrow protected.
              </h3>
              <ul className="space-y-3 text-[14px] text-[#3F3F3F]">
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={16} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>Filter by what matters</strong> — distance, price, breed experience, weekend availability, mobile vs. in-shop, insurance &amp; background-check badges.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={16} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>See actual reviews from verified bookings</strong> — every review comes from a real captured payment, never anonymous drive-bys.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={16} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>Live GPS walk maps (optional).</strong> Turn it on for any walker or sitter — you'll see the dot move on a live map inside your bookings while they're out. Either side can stop sharing in one tap.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={16} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>Recurring without the friction</strong> — book a weekly walker once, the provider auto-charges after each session. Pause or cancel anytime; future-session refunds fire automatically.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={16} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>Zoomie Time — private fields by the hour.</strong> Landowners rent out fenced, distraction-free fields for safe off-leash zoomies. Visits never overlap, the exact address unlocks after booking, and hosts see your dog's vaccination records before you arrive.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={16} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>Cancellation policy is the provider's, not ours.</strong> We surface it before checkout so there are no surprises.</span>
                </li>
              </ul>
              <Link
                to="/find-pros"
                data-testid="landing-service-pros-find-cta"
                className="inline-flex items-center gap-2 mt-6 bg-ink hover:bg-crimson text-white rounded-full px-5 py-3 text-[13.5px] font-bold transition-colors"
              >
                Find a pro near you <ArrowRight size={13} weight="bold" />
              </Link>
            </div>

            <div
              className="bg-ink text-white border border-gold/30 rounded-2xl p-7 relative overflow-hidden"
              data-testid="landing-service-pros-pro-side"
            >
              <NoiseOverlay opacity={0.05} />
              <div className="relative">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold-light mb-2">
                  For service pros
                </p>
                <h3 className="font-spectral text-2xl mb-4 leading-tight">
                  Get found. Get paid. Stay independent.
                </h3>
                <ul className="space-y-3 text-[14px] text-white/85">
                  <li className="flex items-start gap-2.5">
                    <Sparkle size={14} weight="fill" className="text-gold-light mt-1 flex-shrink-0" />
                    <span><strong className="text-white">Keep 88% of every booking.</strong> 12% platform fee comes out of your payout — payouts release after each session, direct to your Stripe Connect account.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Sparkle size={14} weight="fill" className="text-gold-light mt-1 flex-shrink-0" />
                    <span><strong className="text-white">Recurring bookings priced in.</strong> A weekly walker can lock in 26-week revenue with one consent at the first session — auto-charged 24h before each visit.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Sparkle size={14} weight="fill" className="text-gold-light mt-1 flex-shrink-0" />
                    <span><strong className="text-white">Trust badges that matter</strong> — Identity (always), and optional Background Check, Pet First Aid, Insurance, Breed-Specific Experience. Each is reviewed individually and shown to buyers.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Sparkle size={14} weight="fill" className="text-gold-light mt-1 flex-shrink-0" />
                    <span><strong className="text-white">Walkers &amp; sitters: live GPS map.</strong> If the owner opts in on a booking, share a live location map with them during the session — owners love the peace of mind, and it lifts your rating. Sharing is your choice each walk; nothing is stored after 24 hours.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Sparkle size={14} weight="fill" className="text-gold-light mt-1 flex-shrink-0" />
                    <span><strong className="text-white">You&rsquo;re an independent business.</strong> Armstrong K9 Registry is your venue — never your employer. You set hours, prices, areas, and methods.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Sparkle size={14} weight="fill" className="text-gold-light mt-1 flex-shrink-0" />
                    <span><strong className="text-white">Disputes go through Stripe</strong> — escrow protects both sides on every booking.</span>
                  </li>
                </ul>

                {/* Concrete economics example — the old service-providers
                    section had this and it does real work selling the
                    pro side. Kept compact so it reads as a footnote, not
                    a second card. */}
                <div className="mt-6 pt-5 border-t border-white/10 text-[12.5px] text-white/70 leading-relaxed" data-testid="landing-service-pros-economics">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-gold-light font-bold mb-2">A $80 weekly walk, by the numbers</p>
                  <p>
                    Customer pays <span className="font-tabular text-white">$80.00</span> + <span className="font-tabular text-white">$2.40</span> buyer-protection fee.
                    You net <span className="font-tabular text-white">$70.40</span> via Stripe Connect, ~2 business days after the visit clears.
                    Subscribe weekly and that's <span className="font-tabular text-white">$1,830</span> a year per regular client.
                  </p>
                </div>
                <Link
                  to="/provider/apply"
                  data-testid="landing-service-pros-apply-cta"
                  className="inline-flex items-center gap-2 mt-6 bg-gold-gradient hover:opacity-95 text-ink rounded-full px-5 py-3 text-[13.5px] font-bold transition-opacity"
                >
                  Apply to list your services <ArrowRight size={13} weight="bold" />
                </Link>
              </div>
            </div>
          </div>

          {/* Liability strip — small, quiet, but legally indispensable
              on a page that's actively pitching the service-pro side. */}
          <p
            className="mt-10 text-[11.5px] text-[#9A9A9A] leading-relaxed max-w-3xl"
            data-testid="landing-service-pros-venue-disclosure"
          >
            <strong className="text-ink">Venue disclosure:</strong> Armstrong K9 Registry
            connects customers with independent service providers and is not a party to
            any service transaction. Providers — not Armstrong K9 Registry — are
            responsible for performing their services and for carrying whatever
            insurance and licensing their jurisdiction or business requires.
            Insurance, licensing, and background-check details shown on a
            provider profile are self-reported by the provider and{" "}
            <em>strongly recommended</em> for the trades that involve a dog
            leaving the owner&rsquo;s sight (walking, sitting, boarding) but are
            not required by Armstrong K9 Registry to list.
          </p>
        </div>
      </section>

      {/* Zoomie Time — two-sided pitch: landowners (earnings math) and
          dog owners (find a yard near you). Cream section to keep the
          page's light/dark alternation after the dark service-pros block. */}
      <section id="zoomie-time" className="relative py-20 md:py-28 bg-cream border-y border-[#EDE7D3]">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#2E6B3E] mb-3 flex items-center gap-2">
            <Tree size={14} weight="duotone" /> Zoomie Time
          </p>
          <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-3xl">
            Rent a private field for safe, off-leash zoomies.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#5A5A5A] max-w-2xl">
            Reactive dog? Recall still a work in progress? Book a fenced, distraction-free
            field all to yourselves by the hour — no strange dogs, no surprises. And if
            you own land, that quiet back field can quietly pay for itself.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-6 items-stretch">
            {/* Guest side — find a yard */}
            <div className="bg-white rounded-2xl border border-[#EAE3CF] p-7 md:p-8 flex flex-col" data-testid="landing-zoomie-guest-card">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A6A00] mb-3">For dogs who need room to run</p>
              <h3 className="font-spectral text-2xl leading-tight">Find a yard near you</h3>
              <ul className="mt-5 space-y-3 text-[13.5px] leading-relaxed text-[#3A3A3A]">
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={15} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>Search your area</strong> — filter by ZIP, fully fenced, and minimum acreage; browse host photos and field descriptions before you book.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={15} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>Message the host first</strong> — ask about fencing, terrain, or your dog's quirks right from their page.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={15} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>Truly private visits</strong> — hosts leave a gap between guests so you never overlap. The exact address unlocks once your booking is confirmed.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle size={15} weight="fill" className="text-[#3DA56E] mt-0.5 flex-shrink-0" />
                  <span><strong>On your calendar automatically</strong> — every confirmed visit lands on your Armstrong K9 Registry calendar, alongside heat alerts for females in season.</span>
                </li>
              </ul>
              <Link
                to="/find-pros?kind=field_host"
                data-testid="landing-zoomie-guest-cta"
                className="inline-flex items-center justify-center gap-2 mt-7 bg-ink hover:bg-crimson text-white rounded-full px-5 py-3 text-[13.5px] font-bold transition-colors self-start"
              >
                Find a field near you <ArrowRight size={13} weight="bold" />
              </Link>
            </div>

            {/* Host side — earnings math */}
            <div className="bg-ink text-white rounded-2xl p-7 md:p-8 flex flex-col relative overflow-hidden" data-testid="landing-zoomie-host-card">
              <div aria-hidden className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-[#2E6B3E]/30 blur-3xl pointer-events-none" />
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold-light mb-3 relative">Become a Zoomie Time host</p>
              <h3 className="font-spectral text-2xl leading-tight relative">Your land already works. Let it earn.</h3>
              <ul className="mt-5 space-y-3 text-[13.5px] leading-relaxed text-white/85 relative">
                <li className="flex items-start gap-2.5">
                  <Sparkle size={14} weight="fill" className="text-gold-light mt-1 flex-shrink-0" />
                  <span><strong className="text-white">You set the price, hours, and rules.</strong> List your acreage, fencing, photos, and hazards — guests book around your life, with a buffer gap you control between visits.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Sparkle size={14} weight="fill" className="text-gold-light mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Your address stays private</strong> until a guest has booked and paid. Every guest signs a field-use waiver, and you can view their dog's registry profile and vaccination records before they arrive.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Sparkle size={14} weight="fill" className="text-gold-light mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Paid by Stripe direct deposit</strong> — you keep 88% of every visit, ~2 business days after it clears.</span>
                </li>
              </ul>
              <div className="mt-6 pt-5 border-t border-white/10 text-[12.5px] text-white/70 leading-relaxed relative" data-testid="landing-zoomie-earnings-math">
                <p className="text-[10px] uppercase tracking-[0.22em] text-gold-light font-bold mb-2">The back-field math</p>
                <p>
                  Charge <span className="font-tabular text-white">$20</span> an hour and you net{" "}
                  <span className="font-tabular text-white">$17.60</span> per visit.
                  Just <span className="font-tabular text-white">12</span> visits a week —
                  two an evening — is <span className="font-tabular text-white">$211</span> a week,
                  about <span className="font-tabular text-white">$915</span> a month,{" "}
                  <span className="font-tabular text-white">$10,900+</span> a year. For grass you already mow.
                </p>
              </div>
              <Link
                to="/become-a-provider?kind=field_host"
                data-testid="landing-zoomie-host-cta"
                className="inline-flex items-center gap-2 mt-6 bg-gold-gradient hover:opacity-95 text-ink rounded-full px-5 py-3 text-[13.5px] font-bold transition-opacity self-start relative"
              >
                List your land <ArrowRight size={13} weight="bold" />
              </Link>
            </div>
          </div>

          <p className="mt-8 text-[11.5px] text-[#9A9A9A] leading-relaxed max-w-3xl" data-testid="landing-zoomie-disclosure">
            <strong className="text-ink">Zoomie Time disclosure:</strong> hosts are independent
            landowners, not Armstrong K9 Registry. Field details — fencing, terrain, hazards —
            are self-reported by each host and not inspected or verified by the registry.
            Guests certify vaccinations and assume all risk under the Field Use Waiver signed
            at booking; Armstrong K9 Registry provides no damage protection or insurance.
          </p>
        </div>
      </section>


      {/* Calendar — flagship recent build. Dark dramatic frame as
          agreed (preserves the rest of the page's light/cream
          alternation while giving the calendar feature its proper
          stage). The mockup shows persona-aware events all on one
          surface — exactly the magic the feature delivers. */}
      <section className="relative bg-ink text-white overflow-hidden" data-testid="landing-calendar">
        <NoiseOverlay opacity={0.08} />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 85% 25%, rgba(201,162,39,0.20), transparent 55%), radial-gradient(circle at 10% 80%, rgba(193,39,45,0.10), transparent 50%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-[1.05fr,1fr] gap-14 items-center">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-4 flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" /> New
            </p>
            <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-xl">
              The intelligent itinerary.
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed text-white/85 max-w-xl">
              One calendar that knows who you are. Heat cycles auto-pull from
              your dogs. Whelping dates predict from breedings, with the 63-day
              estimate clearly labeled. Transport pickups appear the moment a
              haul is booked. Vet appointments and birthdays sit alongside.
              Push reminders fire about 24 hours ahead.
            </p>
            <ul className="mt-7 space-y-2.5 text-[14.5px] text-white/85">
              <li className="flex items-start gap-2.5">
                <Drop size={16} weight="fill" className="text-[#F87171] mt-0.5 flex-shrink-0" />
                <span>Heat-cycle history and the next predicted heat (180-day average)</span>
              </li>
              <li className="flex items-start gap-2.5">
                <HeartStraight size={16} weight="fill" className="text-[#EC4899] mt-0.5 flex-shrink-0" />
                <span>Breeding dates, plus a tentative whelp date 63 days later — with a clear estimate-only disclaimer</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Truck size={16} weight="fill" className="text-[#60A5FA] mt-0.5 flex-shrink-0" />
                <span>Transport pickups and ETAs, visible to both buyer and transporter</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Bell size={16} weight="fill" className="text-gold-light mt-0.5 flex-shrink-0" />
                <span>Web push reminders ~24h before any event — opt-in, revocable</span>
              </li>
            </ul>
            <div className="mt-9 flex items-center gap-3 flex-wrap">
              <Link to="/register?next=/calendar">
                <Button
                  className="rounded-full bg-gold-gradient hover:opacity-95 text-ink font-semibold h-12 px-7 shadow-[0_8px_24px_rgba(201,162,39,0.35)] border-0"
                  data-testid="calendar-section-cta"
                >
                  Open the calendar <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <span className="text-xs text-white/60">Included on every account.</span>
            </div>
          </div>

          {/* Visual mockup — a dark, gold-edged calendar tile with
              colored event pills demonstrating the persona-aware mix. */}
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 -m-6 rounded-3xl"
              style={{
                background:
                  "radial-gradient(circle at center, rgba(201,162,39,0.25) 0%, transparent 70%)",
              }}
            />
            <div className="relative rounded-2xl border border-gold/30 bg-[#0A0A0A]/90 backdrop-blur-sm shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10">
                <span className="font-spectral text-[15px] text-white">May 2026</span>
                <Calendar size={15} className="text-gold-light" weight="duotone" />
              </div>
              <div className="grid grid-cols-7 text-[9px] uppercase tracking-wider text-white/40 border-b border-white/5 bg-white/[0.02]">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                  <div key={i} className="px-1.5 py-1.5 text-center font-semibold">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 text-[10px]">
                {Array.from({ length: 35 }).map((_, i) => {
                  const day = i - 4; // start the month on Friday May 1
                  const inMonth = day >= 1 && day <= 31;
                  const isToday = day === 21;
                  const events = day === 21
                    ? [{ c: "#EC4899", t: "Bred Ruby × Atlas" }, { c: "#DC2626", t: "Lily heat" }]
                    : day === 23
                    ? [{ c: "#16A34A", t: "Marilyn 3rd b'day" }]
                    : day === 28
                    ? [{ c: "#2563EB", t: "Pickup → Tampa" }]
                    : day === 14
                    ? [{ c: "#9333EA", t: "Video tour 2pm" }]
                    : day === 7
                    ? [{ c: "#525252", t: "Vet — annual" }]
                    : day === 4
                    ? [{ c: "#D4AF37", t: "Whelp predicted (est.)" }]
                    : [];
                  return (
                    <div
                      key={i}
                      className={`min-h-[58px] border-r border-b border-white/5 last:border-r-0 p-1 ${inMonth ? "bg-transparent" : "bg-white/[0.02] text-white/20"}`}
                    >
                      <span className={`block text-[10px] mb-1 ${isToday ? "bg-gold text-ink px-1.5 rounded-full inline-block font-bold" : inMonth ? "text-white/70" : "text-white/30"}`}>
                        {inMonth ? day : ""}
                      </span>
                      {events.map((ev, idx) => (
                        <div
                          key={idx}
                          className="text-[8.5px] leading-tight rounded px-1 py-0.5 mb-0.5 truncate italic-on-tentative"
                          style={{
                            background: `${ev.c}25`,
                            color: ev.c,
                            borderLeft: `2px solid ${ev.c}`,
                          }}
                        >
                          {ev.t}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
              {/* Legend strip at the foot */}
              <div className="flex flex-wrap gap-x-3 gap-y-1.5 px-4 py-3 border-t border-white/10 text-[9px] text-white/55">
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]"/>Heat</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#EC4899]"/>Breeding</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"/>Whelp (est.)</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]"/>Transport</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#9333EA]"/>Video tour</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]"/>Birthday</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Litters — new product callout (2026-05). Sits
          between the calendar feature spotlight and the "How it works"
          funnel so the marketing flow goes: features → new flagship
          product → conversion. */}
      <section className="relative bg-[#FBF7EC] border-y border-[#C9A227]/30" data-testid="landing-upcoming">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24 grid lg:grid-cols-[1.05fr,1fr] gap-14 items-center">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A6A00] mb-4 flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" /> New
            </p>
            <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-xl">
              List planned breedings. Collect waitlist deposits. Host signable contracts.
            </h2>
            <p className="mt-5 text-[15px] md:text-[16px] text-[#3F3F3F] leading-relaxed max-w-xl">
              Publish a planned breeding the moment you make the match.
              Buyers reserve their spot with a deposit that lands{" "}
              <b>directly in your Stripe account</b>. Host your lawyer's
              contract for two-party click-wrap signing. Armstrong is a
              venue — never a party to your sale.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/litters/upcoming/new" data-testid="landing-upcoming-cta-publish">
                <Button className="rounded-full bg-ink hover:bg-crimson text-white h-12 px-6">
                  Plan an upcoming litter <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </Link>
              <Link to="/marketplace/upcoming" data-testid="landing-upcoming-cta-browse">
                <Button variant="outline" className="rounded-full border-ink text-ink hover:bg-ink hover:text-white h-12 px-6">
                  Browse upcoming litters
                </Button>
              </Link>
            </div>
          </div>
          <div className="rounded-2xl border-2 border-[#C9A227] bg-white p-7 shadow-gold-glow">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A6A00] font-bold">
              The economics
            </p>
            <ul className="mt-4 space-y-3 text-[14px]">
              <li className="flex items-baseline justify-between gap-3">
                <span className="text-ink/75">Listing fee · planned breeding</span>
                <span className="font-spectral text-2xl text-ink">$25</span>
              </li>
              <li className="flex items-baseline justify-between gap-3">
                <span className="text-ink/75">Listing fee · expected litter</span>
                <span className="font-spectral text-2xl text-ink">$40</span>
              </li>
              <li className="flex items-baseline justify-between gap-3">
                <span className="text-ink/75">Featured boost · 30 days</span>
                <span className="font-spectral text-2xl text-ink">$15</span>
              </li>
              <li className="flex items-baseline justify-between gap-3 border-t border-stone-200 pt-3">
                <span className="text-ink/75">Deposit fee (you receive net)</span>
                <span className="font-spectral text-2xl text-ink">5% <span className="text-base text-ink/55">/ $25 cap</span></span>
              </li>
            </ul>
            <p className="text-[11.5px] text-ink/55 mt-4 leading-relaxed">
              Deposits flow via Stripe Connect. You're the merchant of
              record; chargebacks land on you, not Armstrong K9 Registry. Refund
              policy is yours; we never auto-refund.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative py-20 md:py-28 bg-white" data-testid="landing-how">
        <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gold-hairline" />
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
            How it works
          </p>
          <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight max-w-3xl leading-[1.1]">
            Pick your path. Most people are signed up in under two minutes.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <PathCard
              eyebrow="If you're buying"
              title="Browse, match, and pay safely."
              steps={[
                "Search the marketplace or use Find My Dog matching",
                "Open a live video tour with the kennel",
                "Pay through Stripe escrow — funds release on transfer",
              ]}
              cta="Browse dogs for sale"
              to="/marketplace"
              testId="path-buyer"
            />
            <PathCard
              eyebrow="If you're breeding"
              title="Register, list, sell — under one roof."
              steps={[
                "Register your dogs with COI-aware pedigree audits",
                "List puppies, litters, or stud services in one click",
                "Get paid through Stripe with verified-kennel placement",
              ]}
              cta="Register your kennel"
              to="/register?intent=breeder"
              highlight
              testId="path-breeder"
            />
            <PathCard
              eyebrow="If you just love your dog"
              title="Track every meal, walk, and milestone."
              steps={[
                "Add your dog (name and breed is enough to start)",
                "Tap-log meals, walks, weight, meds & symptoms",
                "Share a live link with your sitter, print a vet PDF",
              ]}
              cta="Explore Dog Log"
              to="/dog-log"
              testId="path-owner"
            />
          </div>
          <div className="mt-10 flex items-center gap-3 flex-wrap">
            <Link to="/register">
              <Button className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-6" data-testid="how-cta">
                Get started <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/get-app">
              <Button variant="outline" className="rounded-full border-ink/30 text-ink hover:border-gold hover:text-gold h-11 px-6" data-testid="how-cta-get-app">
                <Icon as={DeviceMobile} size="xs" className="mr-2" />
                Install on your phone
              </Button>
            </Link>
            <span className="text-sm text-[#6B6B6B]">Free to register. Upgrade only when you sell, list, or unlock Dog Log.</span>
          </div>
        </div>
      </section>

      {/* Kennel Storefronts — sell direct callout */}
      <section className="py-16 md:py-24 bg-white relative" data-testid="landing-storefronts">
        <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
                <Sparkle size={12} weight="fill" /> New — for breeders
              </div>
              <h2 className="font-spectral text-4xl sm:text-5xl text-ink mt-3">
                Run your own Dog Shop
              </h2>
              <p className="text-[#3a3a3a] mt-4 leading-relaxed">
                Sell dog food, treats, and merch under your own brand — your photos,
                your prices, your local-delivery zone. Payouts go straight to your
                Stripe account; we keep the lights on with a 5% platform fee.
              </p>
              <ul className="mt-5 space-y-2 text-[14px] text-[#3a3a3a]">
                <li>· Your own <span className="font-tabular">/store/your-kennel</span> URL</li>
                <li>· ZIP-gated local delivery + nationwide merch shipping</li>
                <li>· Direct Stripe Connect payouts — you keep the customer</li>
                <li>· Listed in the public <Link to="/storefronts" className="underline">storefront directory</Link></li>
                {!isNativeApp() && <li>· Optional $29/mo Featured rotation for top placement</li>}
              </ul>
              <div className="mt-7 flex items-center gap-3 flex-wrap">
                <Link to="/storefronts">
                  <Button className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-6" data-testid="storefronts-browse-cta">
                    Browse storefronts <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/billing" className="text-[13px] font-semibold text-ink hover:text-crimson underline underline-offset-4">
                  Open my storefront — {storefrontMo}/mo →
                </Link>
              </div>
            </div>
            <div className="rounded-2xl border border-[#E8D9A6] bg-gradient-to-br from-[#FBF7EC] to-white p-6">
              <p className="text-[12px] uppercase tracking-[0.18em] text-gold-dark font-semibold">What a $40 sale looks like</p>
              <div className="mt-3 space-y-2 font-tabular text-[14px]">
                <Row label="Customer pays" value="$40.00" />
                <Row label="Stripe fee (2.9% + $0.30)" value="− $1.46" />
                <Row label="Platform fee (3%)" value="− $1.20" />
                <div className="h-px bg-[#E8D9A6] my-1" />
                <Row label="You net" value="$37.34" bold />
              </div>
              <p className="text-[11px] text-[#6B6B6B] mt-4 leading-relaxed">
                No prorated refunds. Cancel any time. Stripe pays out to your bank ~2 business days after each sale clears.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Armstrong Transport marketplace — third-party haulers */}
      <section className="py-16 md:py-24 bg-cream relative" data-testid="landing-transport">
        <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="rounded-2xl border border-[#E8D9A6] bg-gradient-to-br from-white to-[#FBF7EC] p-6 order-2 lg:order-1">
              <p className="text-[12px] uppercase tracking-[0.18em] text-gold-dark font-semibold">How a Memphis → Atlanta trip works</p>
              <ol className="mt-3 space-y-3 text-[13px] text-[#3a3a3a] leading-relaxed list-decimal pl-5">
                <li>Buyer submits a request — we fan it out to every verified transporter covering the route.</li>
                <li>Multiple haulers submit bids. Buyer picks the one they like.</li>
                <li>Stripe holds the payment in escrow.</li>
                <li>Transporter picks up + delivers. Escrow auto-releases 48h after delivery.</li>
                <li>Disputes? Frozen escrow + a published 30-day mutual-consent or default-refund timeline. Armstrong K9 Registry never picks sides.</li>
              </ol>
              <p className="text-[11px] text-[#6B6B6B] mt-4 leading-relaxed">
                Armstrong is the marketplace. Trips are operated by independent, USDA-licensed transporters under their own insurance.
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
                <Sparkle size={12} weight="fill" /> New — for buyers
              </div>
              <h2 className="font-spectral text-4xl sm:text-5xl text-ink mt-3">
                Ground transport, escrowed
              </h2>
              <p className="text-[#3a3a3a] mt-4 leading-relaxed">
                Buying a dog hundreds of miles away? <strong>Armstrong Transport</strong> is
                our independent-transporter marketplace. Every hauler on the platform is{" "}
                USDA-licensed with at least $300K in commercial-auto liability. Buyer pays
                via Stripe escrow; funds release to the transporter only after delivery is
                confirmed — or after a 48-hour cool-down. Disputes freeze the escrow until
                Armstrong reviews.
              </p>
              <ul className="mt-5 space-y-2 text-[14px] text-[#3a3a3a]">
                <li>· USDA Class B licensed, $300K+ insurance, payouts via Stripe Connect</li>
                <li>· Bid-based pricing — pick the carrier you like</li>
                <li>· Buyer protection: escrow + 48h release + dispute mediation</li>
                <li>· 10% flat platform fee — no hidden markups</li>
              </ul>
              <div className="mt-7 flex items-center gap-3 flex-wrap">
                <Link to="/transport">
                  <Button className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-6" data-testid="transport-browse-cta">
                    Browse transporters <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/transport/request" className="text-[13px] font-semibold text-ink hover:text-crimson underline underline-offset-4" data-testid="transport-request-cta">
                  Request a transport →
                </Link>
                <Link to="/transport/apply" className="text-[13px] font-semibold text-ink hover:text-crimson underline underline-offset-4" data-testid="transport-apply-cta">
                  Become a transporter →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="kennels" className="py-20 md:py-28 bg-cream relative" data-testid="landing-kennels">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gold-hairline" />
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
                Featured kennels
              </p>
              <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.1]">
                Real kennels. Real dogs. Real pedigrees.
              </h2>
            </div>
            <Link to="/breeders" className="text-sm text-ink hover:text-crimson underline underline-offset-4">
              Browse all breeders →
            </Link>
          </div>
          {featured.length > 0 ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5" data-testid="featured-kennels-grid">
              {featured.map((k) => (
                <KennelCard key={k.id} k={k} />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-[#C9A227]/40 bg-white/60 p-10 text-center text-sm text-[#6B6B6B]">
              No verified kennels yet — be the first to register and lock in your kennel name.
            </div>
          )}
        </div>
      </section>

      {/* Journal strip — notes from the registry. Inserted between the
          featured kennels and the final CTA so curious visitors who
          aren't ready to sign up can read the team's editorial voice
          first. Same cream-paper tier as the featured-kennels section
          above, divided only by the gold-hairline separator. */}
      <section className="bg-cream py-16 md:py-24" data-testid="landing-journal">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
                From the journal
              </p>
              <h2 className="font-spectral font-light text-3xl md:text-4xl tracking-tight leading-[1.1] max-w-2xl">
                What we verify, why, and how. Plain language.
              </h2>
            </div>
            <Link to="/journal" className="text-sm text-ink hover:text-crimson underline underline-offset-4">
              Read all →
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { slug: "how-we-verify-a-kennel", title: "How we verify a kennel", dek: "The seven checks behind every gold WaxSeal — and why we walk away from kennels that won't pass them.", section: "Trust & safety", read: 6 },
              { slug: "the-armstrong-k9-standard", title: "The Armstrong K9 standard", dek: "We don't tell breeders how to breed. We tell buyers what 'verified' means here, in writing, with no asterisks.", section: "Editorial", read: 5 },
              { slug: "what-a-six-generation-pedigree-proves", title: "What a 6-generation pedigree actually proves", dek: "Pedigree depth isn't bragging — it's the only reliable way to surface inbreeding risk before a litter is on the ground.", section: "Pedigrees", read: 7 },
            ].map((p) => (
              <Link
                key={p.slug}
                to={`/journal/${p.slug}`}
                className="group block bg-white border border-[#EAEAEA] hover:border-gold/55 rounded-xl p-6 shadow-luxury-light hover:shadow-gold-glow hover:-translate-y-0.5 transition-all duration-500"
                data-testid={`landing-journal-${p.slug}`}
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-3">{p.section}</p>
                <h3 className="font-spectral text-xl tracking-tight leading-tight group-hover:text-crimson transition-colors">
                  {p.title}
                </h3>
                <p className="text-sm text-[#3a3a3a] mt-3 leading-relaxed line-clamp-3">{p.dek}</p>
                <div className="gold-hairline mt-5 mb-3" />
                <p className="text-xs text-[#6B6B6B] font-tabular">{p.read}-min read</p>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="bg-ink text-white relative overflow-hidden" data-testid="landing-cta">
        <NoiseOverlay opacity={0.07} />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, rgba(201,162,39,0.22), transparent 60%)",
          }}
        />
        <div className="pointer-events-none absolute inset-0 bg-vignette-dark" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-24 text-center">
          <GoldDivider variant="ornament" className="max-w-xs mx-auto mb-10 [&>span]:bg-[linear-gradient(90deg,transparent_0%,rgba(201,162,39,0.55)_50%,transparent_100%)]" />
          <h2 className="font-spectral font-light text-4xl md:text-6xl tracking-tight max-w-3xl mx-auto leading-[1.05]">
            Whether you&rsquo;re buying a puppy, building a kennel, or just loving the dog you already have — start here.
          </h2>
          <p className="mt-6 text-white/80 max-w-2xl mx-auto leading-relaxed">
            Free to browse the marketplace. Free to register your dogs. Dog Log keeps the diary your dog deserves. You only pay when
            you list, sell, or upgrade to verified.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/marketplace">
              <Button
                className="rounded-full bg-gold-gradient hover:opacity-95 text-ink font-semibold h-12 px-7 shadow-[0_8px_24px_rgba(201,162,39,0.35)] border-0"
                data-testid="cta-browse-puppies"
              >
                Browse dogs for sale <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/register?intent=breeder">
              <Button
                variant="outline"
                className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-ink h-12 px-6"
                data-testid="cta-register-kennel"
              >
                Register your kennel
              </Button>
            </Link>
            <Link to="/dog-log">
              <Button
                variant="outline"
                className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-ink h-12 px-6"
                data-testid="cta-dog-log"
              >
                Track my dog (Dog Log)
              </Button>
            </Link>
            <Link to="/login">
              <Button
                variant="ghost"
                className="rounded-full text-white/80 hover:text-white hover:bg-white/10 h-12 px-5"
                data-testid="cta-signin"
              >
                I already have an account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter capture — above the footer so visitors who scroll
          past every CTA still land on a conversion offer instead of
          legal links. */}
      <JournalNewsletterCapture source="landing-footer" />

      {/* Global reach — auto-appears when the worldwide toggle flips */}
      <GlobalReachSection />

      {/* Footer */}
      <footer className="bg-white border-t border-[#EAEAEA] py-10 pb-28 md:pb-12">
        {/* "Reach us" card — placed ABOVE the small-link nav so the
            fixed "Made with Emergent" badge (bottom-right, 178×40)
            cannot cover the primary contact info on mobile. Also gives
            D&B / press / members one clear answer to "how do I get a
            human?" */}
        <div className="max-w-6xl mx-auto px-6 md:px-10 mb-8">
          <BusinessContactCard />
        </div>
        <FooterQuickLinks tone="light" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 mt-8">
          <StoreBadges tone="light" source="footer" />
        </div>
        <div className="max-w-6xl mx-auto px-6 md:px-10 mt-8 flex items-center justify-between gap-6 flex-wrap text-sm text-[#6B6B6B]">
          <div className="flex items-center gap-2">
            <img src="/logo-256.png" alt="" className="h-6 w-6 rounded-full object-contain" />
            <span data-testid="footer-legal-entity">© {new Date().getFullYear()} {DBA_NAME}</span>
          </div>
          <nav className="flex items-center gap-5 flex-wrap">
            {/* Contact moved LEFT of the social/legal links so it sits
                clear of the fixed Emergent badge on mobile (badge is
                anchored bottom-right). Duplicate of the BusinessContactCard
                above — intentional redundancy: D&B verifiers spot-check
                multiple footer surfaces. */}
            <Link to="/contact" className="hover:text-ink font-semibold text-ink" data-testid="footer-contact">Contact</Link>
            <Link to="/get-app" className="hover:text-ink font-semibold text-ink" data-testid="footer-get-app">Get the app</Link>
            <Link to="/pricing" className="hover:text-ink" data-testid="footer-pricing">Pricing</Link>
            <a href="https://www.facebook.com/ArmstrongK9Registry" target="_blank" rel="noopener noreferrer" className="hover:text-ink" data-testid="footer-facebook">Facebook</a>
            <a href="https://www.instagram.com/armstrongk9registry" target="_blank" rel="noopener noreferrer" className="hover:text-ink" data-testid="footer-instagram">Instagram</a>
            <Link to="/legal/terms" className="hover:text-ink">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-ink">Privacy</Link>
            <Link to="/legal/refund" className="hover:text-ink">Refunds</Link>
            <Link to="/legal/accessibility" className="hover:text-ink">Accessibility</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function Stat({ icon: IconCmp, value, label, suffix = "" }) {
  return (
    <div className="text-center">
      <Icon as={IconCmp} tone="gold" size="lg" className="mx-auto mb-2" />
      <p className="font-spectral font-light text-3xl md:text-5xl tracking-tight font-tabular">
        {value}{suffix}
      </p>
      <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#6B6B6B] mt-2">
        {label}
      </p>
    </div>
  );
}

/**
 * EditorialStat — used in the stats strip when the underlying integer
 * would read "small / ghost town" (e.g. 1 kennel). Same visual weight
 * as a normal `Stat` so the row still aligns, but the number is
 * replaced with a curated phrase so the trust story remains intact
 * until the count crosses ~10.
 */
function EditorialStat({ icon: IconCmp, title, sub }) {
  return (
    <div className="text-center" data-testid="landing-editorial-stat">
      <Icon as={IconCmp} tone="gold" size="lg" className="mx-auto mb-2" />
      <p className="font-spectral font-light text-2xl md:text-4xl tracking-tight leading-none">
        {title}
      </p>
      <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B] mt-2 max-w-[180px] mx-auto leading-snug">
        {sub}
      </p>
    </div>
  );
}

function Pillar({ icon: IconCmp, title, text, accent }) {
  // Accent is the platform's tone token: "ink" (neutral pillars) or
  // "gold" (the marquee pillar). We map back to the legacy hex prop
  // so the cream/gold tile background still tints correctly.
  const tone = accent === "#C9A227" ? "gold" : "ink";
  return (
    <LuxuryCard interactive className="p-7 group">
      <div
        className="h-11 w-11 rounded-lg flex items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-105"
        style={{ background: `${accent}15`, color: accent }}
      >
        <Icon as={IconCmp} tone={tone} size="lg" />
      </div>
      <h3 className="font-spectral text-xl mb-2 tracking-tight">{title}</h3>
      <p className="text-sm text-[#3a3a3a] leading-relaxed">{text}</p>
      <div className="gold-hairline mt-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </LuxuryCard>
  );
}

/**
 * PersonaCard — replaces the old Pillar layout for the new persona-first
 * structure. Each card answers ONE question for ONE audience: "what's
 * in it for me?" Bulleted features are recipient-self-interest first
 * (revenue / safety / payment / convenience), not feature taxonomy.
 *
 * `highlight` lifts one card with the dark-ink treatment — used to draw
 * the eye to the audience the platform most wants to convert (currently
 * Buyers, since they bring breeders + transporters along with them).
 */
function PersonaCard({ icon: IconCmp, eyebrow, title, features, cta, to, highlight = false }) {
  const cardClass = highlight
    ? "bg-ink text-white border-gold/30"
    : "bg-white text-ink border-[#EAEAEA]";
  const iconBg = highlight
    ? "bg-gold/15 text-gold-light"
    : "bg-[#0E0E0E]/5 text-ink";
  const featureColor = highlight ? "text-white/80" : "text-[#3F3F3F]";
  return (
    <div
      className={`group rounded-2xl border p-7 transition-all duration-300 hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.3)] ${cardClass}`}
      data-testid={`persona-card-${eyebrow.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-105 ${iconBg}`}>
        <IconCmp size={18} weight="duotone" />
      </div>
      <p className={`text-[10px] font-bold uppercase tracking-[0.22em] mb-2 ${highlight ? "text-gold-light" : "text-crimson"}`}>
        {eyebrow}
      </p>
      <h3 className="font-spectral text-[22px] leading-tight mb-4 tracking-tight">{title}</h3>
      <ul className={`space-y-2 text-[13.5px] leading-relaxed mb-6 ${featureColor}`}>
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle size={13} weight="fill" className={`mt-1 flex-shrink-0 ${highlight ? "text-gold-light" : "text-gold"}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to={to}
        className={`inline-flex items-center gap-1.5 text-[13px] font-medium ${highlight ? "text-gold-light hover:text-white" : "text-ink hover:text-crimson"}`}
      >
        {cta} <ArrowRight size={13} />
      </Link>
    </div>
  );
}

function ProVerticalCard({ icon: IconCmp, vertical, typical, examples, note }) {
  return (
    <div
      className="rounded-2xl border border-[#EAEAEA] bg-white p-5 transition hover:border-ink hover:shadow-[0_8px_20px_-12px_rgba(0,0,0,0.25)]"
      data-testid={`pro-vertical-${vertical.toLowerCase()}`}
    >
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-gold-gradient text-ink mb-3">
        <IconCmp size={16} weight="duotone" />
      </span>
      <h3 className="font-spectral text-xl tracking-tight">{vertical}</h3>
      <p className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-bold mt-1">{typical}</p>
      <p className="text-[12.5px] text-[#3F3F3F] mt-2 leading-snug">{examples}</p>
      <p className="text-[11px] text-[#6B6B6B] mt-3 leading-snug italic">{note}</p>
    </div>
  );
}

function ServiceTile({ icon: IconCmp, title, body }) {
  return (
    <div
      className="rounded-xl border border-[#EAEAEA] bg-white p-4 transition hover:border-ink hover:shadow-[0_8px_20px_-12px_rgba(0,0,0,0.25)]"
      data-testid={`service-tile-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#0E0E0E]/5 text-ink">
          <IconCmp size={14} weight="duotone" />
        </span>
        <p className="font-spectral text-[15px] tracking-tight text-ink">{title}</p>
      </div>
      <p className="text-[#3F3F3F] leading-relaxed">{body}</p>
    </div>
  );
}

function Step({ n, title, text }) {
  return (
    <div>
      <p className="font-spectral font-light text-6xl tracking-tight mb-3 bg-gold-gradient bg-clip-text text-transparent leading-none">{n}</p>
      <h3 className="font-spectral text-xl mb-2 tracking-tight">{title}</h3>
      <p className="text-sm text-[#3a3a3a] leading-relaxed">{text}</p>
    </div>
  );
}

/**
 * PathCard — one of three persona-specific paths under "How it works".
 * Buyers, breeders, and dog lovers each get their own card with three
 * concrete steps + a CTA into the right onboarding entry point.
 * The middle card (`highlight`) gets the dark-ink emphasis treatment
 * because Breeder Pro is the highest-LTV path and we want to give it
 * a subtle visual lift without burying the other two.
 */
function PathCard({ eyebrow, title, steps, cta, to, highlight = false, testId }) {
  return (
    <div
      className={`rounded-2xl p-7 transition-all duration-500 ${
        highlight
          ? "bg-ink text-white shadow-[0_18px_40px_-18px_rgba(14,14,14,0.5)]"
          : "bg-white border border-[#EAEAEA] hover:border-gold/55 shadow-luxury-light"
      }`}
      data-testid={testId}
    >
      <p className={`text-[10px] font-bold uppercase tracking-[0.28em] mb-3 ${highlight ? "text-gold-light" : "text-gold-dark"}`}>
        {eyebrow}
      </p>
      <h3 className={`font-spectral text-xl tracking-tight mb-5 leading-snug ${highlight ? "text-white" : "text-ink"}`}>
        {title}
      </h3>
      <ul className={`space-y-2.5 text-sm leading-relaxed mb-6 ${highlight ? "text-white/85" : "text-[#3a3a3a]"}`}>
        {steps.map((s, i) => (
          <li key={s} className="flex items-start gap-2.5">
            <span className={`flex-shrink-0 mt-0.5 inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold ${
              highlight ? "bg-gold text-ink" : "bg-[#FBF7EC] text-gold-dark border border-[#E8E0CC]"
            }`}>
              {i + 1}
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
      <Link to={to}>
        <Button
          className={`w-full rounded-full h-10 ${
            highlight
              ? "bg-gold-gradient hover:opacity-95 text-ink font-semibold border-0"
              : "bg-ink hover:bg-crimson text-white"
          }`}
        >
          {cta} <ArrowRight size={14} weight="bold" className="ml-1.5" />
        </Button>
      </Link>
    </div>
  );
}

function BuyerCard({ icon: IconCmp, title, text, cta, to }) {
  return (
    <Link
      to={to}
      data-testid={`buyer-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className="group block bg-white border border-[#EAEAEA] hover:border-gold/55 rounded-xl p-7 shadow-luxury-light hover:shadow-gold-glow hover:-translate-y-0.5 transition-all duration-500"
    >
      <Icon as={IconCmp} tone="gold" size="md" weight="duotone" className="mb-4" />
      <h3 className="font-spectral text-xl tracking-tight mb-2">{title}</h3>
      <p className="text-sm text-[#3a3a3a] leading-relaxed">{text}</p>
      <div className="gold-hairline mt-5 mb-3" />
      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink group-hover:text-crimson transition-colors">
        {cta}
        <ArrowRight size={14} weight="bold" />
      </span>
    </Link>
  );
}

function KennelCard({ k }) {
  const slug = k.slug || k.id;
  const isVerified = k.verified || k.is_verified || k.verified_at;
  return (
    <Link to={`/k/${slug}`} className="block group" data-testid={`kennel-card-${slug}`}>
      <LuxuryCard interactive className="p-5">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-cream flex items-center justify-center flex-shrink-0 overflow-hidden ring-1 ring-[#E2DAC2]">
            {k.avatar_url ? (
              <img src={k.avatar_url} alt="" className="h-full w-full object-cover photo-luxury" />
            ) : (
              <Users size={20} weight="duotone" className="text-[#6B6B6B]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-spectral text-lg tracking-tight truncate group-hover:text-crimson transition-colors">
                {k.kennel_name || k.name}
              </p>
              {isVerified && <WaxSeal size={18} title="Verified kennel" />}
            </div>
            {k.location && (
              <p className="text-xs text-[#6B6B6B] truncate">{k.location}</p>
            )}
            {(k.dog_count !== undefined || k.dogs_count !== undefined) && (
              <p className="text-xs text-[#6B6B6B] mt-1 font-tabular">
                {k.dog_count ?? k.dogs_count} dogs registered
              </p>
            )}
          </div>
        </div>
      </LuxuryCard>
    </Link>
  );
}

/**
 * Pinterest-style breed search hero overlay.
 *
 * Shipped on the landing-page hero as the primary conversion driver:
 * a single autocomplete input that loads the public breed catalog
 * once and filters client-side. On submit (or click-to-pick) we route
 * unauthed visitors to `/register?breed=<name>` so the breed is
 * preserved as signup intent + downstream filter.
 *
 * Why route to /register and not /marketplace:
 *   • /marketplace is auth-gated, the bounce kills conversion
 *   • Sending intent through register lets us prefill the buyer's
 *     interest + drop them into a filtered marketplace post-signup
 *   • Increases lead capture vs. "browse first, register later"
 */
function BreedSearchHero() {
  const [query, setQuery] = useState("");
  const [breeds, setBreeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Pull the public breed catalog once. The endpoint returns an array
  // of strings *or* objects with `{name, group}` — normalize to objects.
  useEffect(() => {
    api.get("/public/breeds")
      .then((r) => {
        const raw = r.data?.breeds || [];
        setBreeds(
          raw.map((b) =>
            typeof b === "string" ? { name: b } : { name: b?.name || "", group: b?.group }
          ).filter((b) => b.name)
        );
      })
      .catch(() => {});
  }, []);

  // Close dropdown on outside click — the dropdown is absolutely
  // positioned so we can't rely on a portal.
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return breeds.slice(0, 8);
    return breeds
      .filter((b) => (b.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, breeds]);

  // Has the user typed a breed that's NOT in our catalog? Drives the
  // "be the first verified X breeder" seller-conversion path.
  const hasNoMatch = useMemo(() => {
    const q = query.trim();
    return q.length >= 2 && breeds.length > 0 && matches.length === 0;
  }, [query, breeds.length, matches.length]);

  // Reset highlight when the matches list changes.
  useEffect(() => { setActiveIdx(0); }, [matches.length, query]);

  const goToBreed = (breedName, opts = {}) => {
    if (!breedName) return;
    const params = new URLSearchParams();
    params.set("breed", breedName);
    if (opts.seller) params.set("intent", "seller");
    navigate(`/register?${params.toString()}`);
  };

  const handleSubmit = () => {
    const q = query.trim();
    // 1. If a match is highlighted, follow it (buyer path).
    if (matches[activeIdx]) {
      goToBreed(matches[activeIdx].name);
      return;
    }
    // 2. No match → seller path: become the first verified X breeder.
    if (q) {
      goToBreed(q, { seller: true });
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid="breed-search-hero">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/60 mb-3">
        Looking for a specific breed?
      </p>
      <div className="flex items-stretch rounded-full bg-white shadow-[0_12px_40px_rgba(0,0,0,0.45)] overflow-hidden ring-1 ring-white/10 focus-within:ring-2 focus-within:ring-gold transition-shadow">
        <span className="pl-5 pr-2 flex items-center text-[#6B6B6B]">
          <MagnifyingGlass size={18} weight="bold" />
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Find a verified Cane Corso breeder…"
          className="flex-1 bg-transparent py-3.5 text-ink placeholder-[#9A9A9A] text-[15px] focus:outline-none"
          data-testid="breed-search-input"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!query.trim() && !matches[0]}
          className="bg-gold-gradient hover:opacity-95 text-ink font-semibold px-5 sm:px-6 disabled:opacity-50 flex items-center gap-1.5 text-[14px] transition-opacity"
          data-testid="breed-search-go"
        >
          <span className="hidden sm:inline">Search</span>
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>

      {open && (matches.length > 0 || hasNoMatch) && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+8px)] bg-white text-ink rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.35)] border border-[#EAEAEA] overflow-hidden z-30 max-h-80 overflow-y-auto"
          data-testid="breed-search-dropdown"
        >
          {matches.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#6B6B6B] px-4 pt-3 pb-2">
                {query.trim() ? `Matches for "${query.trim()}"` : "Popular breeds"}
              </p>
              {matches.map((b, i) => (
                <button
                  key={b.id || b.slug || b.name}
                  type="button"
                  onClick={() => goToBreed(b.name)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
                    i === activeIdx ? "bg-[#FAF6EE]" : "hover:bg-[#FAFAFA]"
                  }`}
                  data-testid={`breed-search-pick-${(b.name || "").toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <Dog size={16} weight="duotone" className="text-gold flex-shrink-0" />
                    <span className="truncate font-medium">{b.name}</span>
                    {b.group && (
                      <span className="text-xs text-[#6B6B6B] truncate hidden sm:inline">
                        · {b.group}
                      </span>
                    )}
                  </span>
                  <ArrowRight size={14} className="text-[#6B6B6B] flex-shrink-0" />
                </button>
              ))}
            </>
          )}

          {hasNoMatch && (
            <button
              type="button"
              onClick={() => goToBreed(query.trim(), { seller: true })}
              className="w-full text-left px-4 py-4 flex items-center justify-between gap-3 bg-[#0E0E0E] text-white hover:bg-[#1a1a1a] transition-colors border-t border-[#EAEAEA]"
              data-testid="breed-search-pick-seller"
            >
              <span className="flex items-start gap-2.5 min-w-0">
                <Sparkle size={16} weight="fill" className="text-gold flex-shrink-0 mt-0.5" />
                <span className="min-w-0">
                  <span className="block text-[10px] uppercase tracking-[0.2em] font-bold text-gold mb-0.5">
                    Be the first
                  </span>
                  <span className="block text-sm font-medium truncate">
                    Register the first verified <strong>"{query.trim()}"</strong> kennel
                  </span>
                </span>
              </span>
              <ArrowRight size={14} weight="bold" className="text-gold flex-shrink-0" />
            </button>
          )}
        </div>
      )}
      <p className="text-[11px] text-white/55 mt-3 leading-relaxed">
        Type any breed and we'll route you to verified kennels working that line.
      </p>
    </div>
  );
}



/**
 * Featured Media strip rendered on the public landing page.
 *
 * Reads `GET /api/media/featured?limit=3`. If the response is empty
 * (no items featured yet), the entire section renders nothing — we
 * never want a hollow "coming soon" block on the homepage.
 *
 * Each card links to `/media/<slug>`, which is also the URL the
 * operator can paste into emails / texts / radio-spot landings.
 */
function LandingMediaStrip() {
  const [items, setItems] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    api.get("/media/featured?limit=3")
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => { /* silent — landing should still render */ })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);
  if (!loaded || items.length === 0) return null;
  const resolve = (u) => {
    if (!u) return "";
    if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
    return u;
  };
  return (
    <section className="relative bg-cream py-16 md:py-20" data-testid="landing-media-strip">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-2">
              Media & Press
            </p>
            <h2 className="font-spectral font-light text-3xl md:text-4xl tracking-tight max-w-2xl leading-[1.15]">
              See the registry in action.
            </h2>
            <p className="text-sm text-[#3F3F3F] mt-3 max-w-2xl">
              Watch our promo videos, download flyers, hear from breeders and buyers — everything we publish, all on one page.
            </p>
          </div>
          <Link
            to="/media"
            className="inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide text-ink hover:text-[#C1272D]"
            data-testid="landing-media-strip-view-all"
          >
            View the full library <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => {
            const cover = resolve(it.cover_url);
            const isVideo = it.kind === "video_file" || it.kind === "video_embed";
            return (
              <Link
                key={it.id}
                to={`/media/${it.slug}`}
                className="group block bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`landing-media-card-${it.slug}`}
              >
                <div className="relative aspect-video bg-[#F4F4F4] overflow-hidden">
                  {cover ? (
                    <img
                      src={cover}
                      alt={it.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0E0E0E] to-[#1f1f1f]" />
                  )}
                  {isVideo && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/90 grid place-items-center shadow-xl group-hover:scale-110 transition-transform">
                        <div className="w-0 h-0 border-y-8 border-y-transparent border-l-[14px] border-l-ink ml-1" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-bold">
                    {({ promo_video: "Promo", flyer: "Flyer", testimonial: "Testimonial", how_to: "How-To", press: "Press", other: "Media" })[it.category] || "Media"}
                  </p>
                  <h3 className="font-spectral text-lg mt-1 leading-tight group-hover:text-[#C1272D] transition-colors">
                    {it.title}
                  </h3>
                  {it.subtitle && (
                    <p className="text-sm text-[#666] mt-1.5 line-clamp-2">{it.subtitle}</p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
